import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { frontConfig } from '../../config';
import { useConnectedRouter } from '../../hooks/useConnectedRouter';
import { useAuthProfile } from '../../hooks/usePermissions';
import { getPageFromQuery } from '../../hooks/useQueryPage';
import { useWebsite } from '../../hooks/useWebsite';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { dispatchOther } from '../../redux/redux.utils';
import {
  addYakliMessageToParent,
  updateYakliMessage,
  updateYakliPagination,
} from '../../redux/slices/yakli-slice';
import type { ChildMessages, Message, Website } from '../../types/api';
import type { AppApolloClient } from '../../types/common-models';
import { handleError } from '../../utils/common-utils';
import type {
  CreateOrUpdateMessageVariables,
  CreateOrUpdateMessageVariablesSmall,
} from '../api/message';
import { createOrUpdateMessage, findThreadIdFromMessageId } from '../api/message';
import type { ThreadMessagesSortType } from '../ThreadMessages/query';
import { sortKeyMapApiToFront, threadMessagesSortTypeValue } from '../ThreadMessages/query';
import { addMessageToParent, updateMessage, updatePagination } from './thread-slice';
import { getStateMessage } from './thread-slice-utils';

export const newResponseKeyInBbf = '__new_response__';

export function getThreadMessagesCountPerPage(website: Website | undefined) {
  if (!website) console.warn('[getThreadMessagesCountPerPage] undefined website');
  const { graphdebate = {} } = website || {};
  const { countPerPage = 10 } = graphdebate;
  return countPerPage;
}

export function useMessagesQueryPageOffsetAndLimit(isYakliDisplay = false) {
  const { query = {} } = useRouter();
  const website = useWebsite();
  const limit = getThreadMessagesCountPerPage(website);

  const page = getPageFromQuery(query);
  const offset = (page - 1) * limit;

  // Store on redux to use later without needing to re-render components, e.g. when submitting a new message.
  const dispatch = useAppDispatch();
  useEffect(() => {
    const dispatcher = isYakliDisplay ? updateYakliPagination : updatePagination;
    dispatch(dispatcher({ page, offset, limit }));
  }, [dispatch, isYakliDisplay, limit, offset, page]);

  return { page, offset, limit };
}

export async function submitAnswer(
  slug: string | undefined,
  messageId: string | undefined,
  client: AppApolloClient,
  variables: CreateOrUpdateMessageVariablesSmall,
  isAnswerToParent: boolean,
  isYakliDisplay?: boolean,
) {
  if (!slug) {
    throw new Error('Cannot answer a topic without slug');
  }

  const threadId = await findThreadIdFromMessageId(
    { slug, messageId: messageId as string },
    client,
  );

  if (!threadId) {
    throw new Error('Cannot answer a thread without id');
  }

  const variables2: CreateOrUpdateMessageVariables = {
    ...variables,
    threadId,
  };
  if (messageId) {
    if (isAnswerToParent) {
      variables2.parentId = messageId;
    } else {
      variables2.messageId = messageId;
    }
  }
  const result = await createOrUpdateMessage(variables2, client);

  if (isAnswerToParent) {
    const addMessageSelector = isYakliDisplay ? addYakliMessageToParent : addMessageToParent;
    dispatchOther(addMessageSelector({ message: result, parentMessageId: messageId }));
  } else {
    if (!messageId)
      throw new Error(`[bug] no message ID but a message was updated: ${JSON.stringify(result)}`);
    const updateMessageSelector = isYakliDisplay ? updateYakliMessage : updateMessage;
    dispatchOther(updateMessageSelector({ message: result }));
  }

  return result;
}

export function getMessagesSortedByRelevance(childMessages: ChildMessages | undefined) {
  if (!childMessages?.messages) return undefined;
  if (!childMessages.relevanceMessageOrder)
    return childMessages.messages.filter(msg => !!msg) as Message[];
  const msgById = new Map<string, Message>();
  for (const msg of childMessages?.messages) {
    if (msg?.id) {
      msgById.set(msg.id, msg);
    }
  }
  const sortedMessages: Message[] = [];
  for (const msg of childMessages.relevanceMessageOrder) {
    if (msg?.id) {
      const fullMsg = msgById.get(msg?.id);
      if (fullMsg) {
        sortedMessages.push(fullMsg);
      }
    }
  }
  return sortedMessages;
}

export function useNavToAnswer() {
  const { query, push } = useConnectedRouter();

  return useCallback(
    (messageId: string | undefined) => {
      if (!messageId && frontConfig.IS_DEV) {
        throw new Error('useNavToAnswer: messageId is undefined. Unexpected? Bug? To investigate.');
      }
      push({
        pathname: messageId ? 'message' : 'topic',
        query: {
          back: true,
          answer: true,
          slug: query.slug as string,
          ...(messageId ? { messageId } : null),
        },
      });
    },
    [push, query.slug],
  );
}

export function useSortMessagesByNewest(slug: string | undefined) {
  const { push } = useConnectedRouter();
  const sortMessagesByNewest = useCallback(() => {
    if (!slug && frontConfig.IS_DEV) {
      const err = new Error('No slug when calling useSortMessagesByNewest. Bug? To investigate.');
      if (frontConfig.IS_DEV) {
        throw err;
      } else {
        handleError(err);
      }
      return;
    }
    setTimeout(() =>
      push({ pathname: '/t/[slug]', query: { slug, sort: 'newest' } }, undefined, {
        shallow: true,
        scroll: false,
      }),
    );
  }, [slug, push]);
  return sortMessagesByNewest;
}

export function useMessageRedux(messageId: string | undefined, isYakliDisplay?: boolean) {
  // TODO use `getStateMessageStrict` and always assume the message exists? (I.e. throw an explicit error if the message is not found in the state.)
  const reduxSliceToHandle = isYakliDisplay ? 'yakli' : 'threads';
  return useAppSelector(state => getStateMessage(state[reduxSliceToHandle].messages, messageId));
}

const TOPIC_DEFAULT_SORT: ThreadMessagesSortType = 'relevance';

export function useTopicMessagesSort() {
  const { query } = useRouter();
  const { profile } = useAuthProfile();
  const { graphdebate } = useWebsite();
  let { sort: defaultBOSort } = graphdebate || {};
  defaultBOSort = defaultBOSort ? sortKeyMapApiToFront[defaultBOSort] : undefined;
  const { sort: rawSort } = query;
  const userSort = profile?.sort;

  const isValidSort = threadMessagesSortTypeValue.includes(rawSort as any);
  const sort = (
    isValidSort ? rawSort : userSort || defaultBOSort || TOPIC_DEFAULT_SORT
  ) as ThreadMessagesSortType;
  return sort;
}

export function mkMessageDOMId(messageId: string | undefined) {
  return messageId ? `msg_${messageId}` : undefined;
}
